import { BaseComponentImpl } from '../../../../types/implementations';
import { Building } from '../../../../types/protocols';
import { ComponentLifetime, MaintenanceTemplate, TemplatesByType, getIntervalForDirection } from '../../TemplatesEditor/types';
import { formatDateForStorage } from '../../../../Helpers/DateUtils';
import { formatDateToString } from '../../../../Helpers/maintenanceCostUtils';
import { overdueToStartYear } from '../../../../Helpers/maintenanceSchedule';
import type { ComponentMaintenanceData, MaintenanceActivity } from '../../../../Helpers/maintenanceCostUtils';
import { wellFormedComponents } from '../../../../Helpers/componentIntegrity';
import { isConditionSet } from '../../../../Helpers/conditionUtils';
import { MaintenanceDraftWithDone } from '../types';
import { calculateReplacementDate, calculateNextMaintenanceDate } from './calculations';
import { resolveCardinalDirection } from './cardinalDirectionResolver';
import { templateOfferedTo } from '../../../../Helpers/maintenanceTemplates';

/**
 * Bring a draft in line with the building it belongs to.
 *
 * The stored entry is the truth the card, the preview and activation read
 * (through `resolveComponentSchedule`), so what the building and the template
 * config imply must be written into it rather than derived on every read:
 *
 * - a component the list shows (well-formed, not archived, typed) that has a
 *   condition (0 included: replace now) and a lifetime gets an entry; an entry whose component the list
 *   no longer shows is removed
 * - the type's own templates for this building get an activity when they have
 *   none (a template created after the draft); an activity whose template is
 *   gone is removed; one with a date or interval missing (a row just added
 *   from the template search) has them filled in
 * - a type template the user removed from the component (`removedActivities`)
 *   gets no activity; a row an older draft switched off (`disabled`) is removed
 * - a replacement year or a maintenance date that has passed is due now
 *
 * User choices are kept: a removed activity stays removed, custom costs,
 * quantities, comments and done/excluded flags are untouched, and a stored
 * date or year is never recomputed from the condition here (that is the
 * condition modal's job).
 *
 * The same rules create a first draft: syncing an empty draft is initialising it.
 */

export interface DraftSyncOptions {
    draft: MaintenanceDraftWithDone;
    building: Building;
    componentLifetimes: Record<string, ComponentLifetime>;
    /** Templates by `primaryComponentType` */
    maintenanceTemplates: TemplatesByType;
    /** Every template by id, for activities on templates of another type */
    findTemplate: (templateId: string) => MaintenanceTemplate | undefined;
    now?: Date;
}

export interface DraftSyncResult {
    draft: MaintenanceDraftWithDone;
    changed: boolean;
    /** Component keys that got a new entry */
    added: string[];
    /** Component keys whose entry was removed */
    removed: string[];
    /** Component keys whose existing entry was completed or corrected */
    updated: string[];
}

export const syncDraftWithBuilding = (options: DraftSyncOptions): DraftSyncResult => {
    const { draft, building, componentLifetimes, maintenanceTemplates, findTemplate } = options;
    const now = options.now ?? new Date();
    const currentYear = now.getFullYear();

    const components: Record<string, ComponentMaintenanceData> = {};
    const added: string[] = [];
    const removed: string[] = [];
    const updated: string[] = [];

    const listed = wellFormedComponents(building.model?.structure?.components)
        .filter(data => !data.archivedAt && data.specificType?.value);

    listed.forEach(componentData => {
        const component = new BaseComponentImpl(componentData);
        const componentType = component.specificType!.value!;
        const lifetime = componentLifetimes[componentType];
        const existing = draft.components[component.key];

        if (!existing) {
            // Same gate as the first draft: nothing to plan without a condition and a lifetime
            if (!isConditionSet(component.condition?.value?.value) || !lifetime) return;
            components[component.key] = newComponentEntry(component, lifetime, building, maintenanceTemplates[componentType] || [], now);
            added.push(component.key);
            return;
        }

        const synced = syncComponentEntry(existing, component, lifetime, building, maintenanceTemplates[componentType] || [], findTemplate, currentYear, now);
        components[component.key] = synced.entry;
        if (synced.changed) updated.push(component.key);
    });

    Object.keys(draft.components).forEach(componentKey => {
        if (!components[componentKey]) removed.push(componentKey);
    });

    const changed = added.length > 0 || removed.length > 0 || updated.length > 0;
    return {
        draft: changed ? { ...draft, components } : draft,
        changed,
        added,
        removed,
        updated
    };
};

/** A fresh entry: every template of the type scheduled from the condition, no costs stored (they are template-sourced until activation). */
export const newComponentEntry = (
    component: BaseComponentImpl,
    lifetime: ComponentLifetime,
    building: Building,
    typeTemplates: MaintenanceTemplate[],
    now: Date = new Date()
): ComponentMaintenanceData => {
    const maintenanceActivities: Record<string, MaintenanceActivity> = {};
    typeTemplates.forEach(template => {
        if (!template?.id) return;
        if (!templateOfferedTo(template, building.key)) return;
        maintenanceActivities[template.id] = newActivityEntry(component, template, lifetime, building, now);
    });

    const entry: ComponentMaintenanceData = { maintenanceActivities };
    const replacementDate = calculateReplacementDate(component, lifetime, building);
    if (replacementDate) {
        entry.replacementYear = replacementDate.getFullYear();
    }
    return entry;
};

/**
 * A template was just created for a component type: every component of that
 * type in the draft gets its activity now, as the sync would give it on the
 * next load. Activities already there and rows the user removed are left as they are.
 */
export const addTemplateToDraft = (
    draft: MaintenanceDraftWithDone,
    building: Building,
    componentLifetimes: Record<string, ComponentLifetime>,
    template: MaintenanceTemplate,
    now: Date = new Date()
): MaintenanceDraftWithDone => {
    if (!template.id) return draft;
    if (!templateOfferedTo(template, building.key)) return draft;

    const buildingComponents = building.model?.structure?.components ?? {};
    let changed = false;
    const components = { ...draft.components };

    Object.entries(draft.components).forEach(([componentKey, entry]) => {
        const componentData = buildingComponents[componentKey];
        if (!componentData || componentData.specificType?.value !== template.primaryComponentType) return;
        if (entry.maintenanceActivities?.[template.id] || entry.removedActivities?.[template.id]) return;

        const component = new BaseComponentImpl(componentData);
        components[componentKey] = {
            ...entry,
            maintenanceActivities: {
                ...entry.maintenanceActivities,
                [template.id]: newActivityEntry(component, template, componentLifetimes[template.primaryComponentType], building, now)
            }
        };
        changed = true;
    });

    return changed ? { ...draft, components } : draft;
};

/**
 * A template was just edited: an activity copies the template's interval when
 * it is created, so every row of the template still on the old interval for its
 * component's direction moves to the new one. A row with its own interval is left as it is.
 */
export const applyTemplateIntervalChange = (
    draft: MaintenanceDraftWithDone,
    building: Building,
    before: MaintenanceTemplate,
    after: MaintenanceTemplate
): MaintenanceDraftWithDone => {
    const buildingComponents = building.model?.structure?.components ?? {};
    let changed = false;
    const components = { ...draft.components };

    Object.entries(draft.components).forEach(([componentKey, entry]) => {
        const activity = entry.maintenanceActivities?.[after.id];
        const componentData = buildingComponents[componentKey];
        if (!activity || !componentData) return;

        const direction = resolveCardinalDirection(new BaseComponentImpl(componentData), building);
        const old = getIntervalForDirection(before, direction);
        const next = getIntervalForDirection(after, direction);
        if (activity.intervalValue !== old.interval_value || activity.intervalType !== old.interval_type) return;
        if (next.interval_value === old.interval_value && next.interval_type === old.interval_type) return;

        components[componentKey] = {
            ...entry,
            maintenanceActivities: {
                ...entry.maintenanceActivities,
                [after.id]: { ...activity, intervalValue: next.interval_value, intervalType: next.interval_type }
            }
        };
        changed = true;
    });

    return changed ? { ...draft, components } : draft;
};

/** The rows of `componentKeys` the template edit leaves on their own values */
export interface TemplateOverridesLeft {
    /** Rows on an own interval while the template's interval for their direction changed */
    interval: string[];
    /** Rows on an own cost (anpassad schablon or offert) while the template's cost or unit changed */
    cost: string[];
}

/**
 * A template was just edited from the row of `componentKeys` (the selected ones in
 * Massredigering): the rows the edit does not reach because they carry their own
 * values. A row on its own interval matches neither the old nor the new template
 * interval for its direction, so this reads the same before and after
 * `applyTemplateIntervalChange`. Nothing is reported for a value the edit left as it was.
 */
export const overridesLeftByTemplateChange = (
    draft: MaintenanceDraftWithDone,
    building: Building,
    before: MaintenanceTemplate,
    after: MaintenanceTemplate,
    componentKeys: string[]
): TemplateOverridesLeft => {
    const buildingComponents = building.model?.structure?.components ?? {};
    const costChanged = before.cost !== after.cost || before.cost_unit !== after.cost_unit;
    const left: TemplateOverridesLeft = { interval: [], cost: [] };

    componentKeys.forEach(componentKey => {
        const activity = draft.components[componentKey]?.maintenanceActivities?.[after.id];
        const componentData = buildingComponents[componentKey];
        if (!activity || !componentData) return;

        const direction = resolveCardinalDirection(new BaseComponentImpl(componentData), building);
        const old = getIntervalForDirection(before, direction);
        const next = getIntervalForDirection(after, direction);
        const sameAs = (interval: ReturnType<typeof getIntervalForDirection>) =>
            activity.intervalValue === interval.interval_value && activity.intervalType === interval.interval_type;
        if (!sameAs(old) && !sameAs(next) && (next.interval_value !== old.interval_value || next.interval_type !== old.interval_type)) {
            left.interval.push(componentKey);
        }

        if (costChanged && (activity.costSource === 'custom' || activity.costSource === 'customRate')) {
            left.cost.push(componentKey);
        }
    });

    return left;
};

/** The rows of `componentKeys` take the template's interval for their component's direction. */
export const followTemplateInterval = (
    draft: MaintenanceDraftWithDone,
    building: Building,
    template: MaintenanceTemplate,
    componentKeys: string[]
): MaintenanceDraftWithDone => {
    const buildingComponents = building.model?.structure?.components ?? {};
    const lastUpdated = formatDateForStorage(new Date());
    let changed = false;
    const components = { ...draft.components };

    componentKeys.forEach(componentKey => {
        const entry = draft.components[componentKey];
        const activity = entry?.maintenanceActivities?.[template.id];
        const componentData = buildingComponents[componentKey];
        if (!entry || !activity || !componentData) return;

        const next = getIntervalForDirection(template, resolveCardinalDirection(new BaseComponentImpl(componentData), building));
        if (activity.intervalValue === next.interval_value && activity.intervalType === next.interval_type) return;

        components[componentKey] = {
            ...entry,
            maintenanceActivities: {
                ...entry.maintenanceActivities,
                [template.id]: { ...activity, intervalValue: next.interval_value, intervalType: next.interval_type, lastUpdated }
            }
        };
        changed = true;
    });

    return changed ? { ...draft, components } : draft;
};

/** A fresh activity: next date from the condition, interval from the template for the component's direction. */
export const newActivityEntry = (
    component: BaseComponentImpl,
    template: MaintenanceTemplate,
    lifetime: ComponentLifetime | undefined,
    building: Building,
    now: Date = new Date()
): MaintenanceActivity => {
    const interval = getIntervalForDirection(template, resolveCardinalDirection(component, building));
    const nextDate = lifetime ? calculateNextMaintenanceDate(component, template, lifetime, building) : null;
    return {
        nextDate: formatDateToString(nextDate || now),
        intervalType: interval.interval_type,
        intervalValue: interval.interval_value,
        lastUpdated: formatDateForStorage(now)
    };
};

/**
 * The activity a component's row starts from, as the sync would write it: the
 * template is one of the type's own or, for a row added from the template
 * search, any type's. Null when the component or the template is gone.
 */
export const activityDefaults = (
    building: Building,
    componentKey: string,
    templateId: string,
    componentLifetimes: Record<string, ComponentLifetime>,
    maintenanceTemplates: TemplatesByType,
    findTemplate: (templateId: string) => MaintenanceTemplate | null | undefined,
    now: Date = new Date()
): MaintenanceActivity | null => {
    const componentData = building.model?.structure?.components?.[componentKey];
    const componentType = componentData?.specificType?.value;
    if (!componentData || !componentType) return null;

    const template = (maintenanceTemplates[componentType] || []).find(t => t?.id === templateId) ?? findTemplate(templateId);
    if (!template) return null;

    return newActivityEntry(new BaseComponentImpl(componentData), template, componentLifetimes[componentType], building, now);
};

const syncComponentEntry = (
    existing: ComponentMaintenanceData,
    component: BaseComponentImpl,
    lifetime: ComponentLifetime | undefined,
    building: Building,
    typeTemplates: MaintenanceTemplate[],
    findTemplate: (templateId: string) => MaintenanceTemplate | undefined,
    currentYear: number,
    now: Date
): { entry: ComponentMaintenanceData; changed: boolean } => {
    let changed = false;
    const entry: ComponentMaintenanceData = { ...existing };

    // Replacement year: fill from the condition when missing, pull an overdue one to this year
    if (entry.replacementYear === undefined || entry.replacementYear === null) {
        const replacementDate = lifetime ? calculateReplacementDate(component, lifetime, building) : null;
        if (replacementDate) {
            entry.replacementYear = replacementDate.getFullYear();
            changed = true;
        }
    } else if (entry.replacementYear < currentYear) {
        entry.replacementYear = currentYear;
        changed = true;
    }

    const activities: Record<string, MaintenanceActivity> = { ...(existing.maintenanceActivities || {}) };

    // A removal marker only means something for one of the type's own templates
    const removed = { ...(existing.removedActivities || {}) };
    Object.keys(removed).forEach(templateId => {
        if (typeTemplates.some(t => t?.id === templateId)) return;
        delete removed[templateId];
        changed = true;
    });

    // Rows could be switched off before they could be removed; one left switched off is removed
    Object.entries(activities).forEach(([templateId, activity]) => {
        if (activity.disabled !== true) return;
        delete activities[templateId];
        if (typeTemplates.some(t => t?.id === templateId)) removed[templateId] = true;
        changed = true;
    });

    typeTemplates.forEach(template => {
        if (!template?.id || activities[template.id] || removed[template.id]) return;
        if (!templateOfferedTo(template, building.key)) return;
        activities[template.id] = newActivityEntry(component, template, lifetime, building, now);
        changed = true;
    });

    Object.entries(activities).forEach(([templateId, activity]) => {
        const template = typeTemplates.find(t => t.id === templateId) ?? findTemplate(templateId);
        if (!template) {
            delete activities[templateId];
            changed = true;
            return;
        }
        const hasDate = typeof activity.nextDate === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(activity.nextDate);
        const hasInterval = typeof activity.intervalValue === 'number' && activity.intervalValue > 0 && isFinite(activity.intervalValue) && !!activity.intervalType;
        // An overdue date is due this year, as the resolver schedules it
        const dueDate = hasDate ? overdueToStartYear(activity.nextDate, currentYear) : undefined;
        if (hasDate && hasInterval && dueDate === activity.nextDate) return;

        const fresh = newActivityEntry(component, template, lifetime, building, now);
        activities[templateId] = {
            ...activity,
            nextDate: dueDate ?? fresh.nextDate,
            intervalType: hasInterval ? activity.intervalType : fresh.intervalType,
            intervalValue: hasInterval ? activity.intervalValue : fresh.intervalValue,
            lastUpdated: fresh.lastUpdated
        };
        changed = true;
    });

    entry.maintenanceActivities = activities;
    if (Object.keys(removed).length > 0) {
        entry.removedActivities = removed;
    } else {
        delete entry.removedActivities;
    }
    return { entry, changed };
};
